import React from "react";
import { motion } from "framer-motion";
import { Instagram, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

/**
 * Instagram-styled preview strip for a venue.
 * Displays curated venue photos in a polished social-feed grid with a strong "Follow" CTA.
 * Honest: this links to the venue's actual Instagram profile (no live API pull).
 */
export default function InstagramPreview({ venue }) {
  if (!venue?.instagram) return null;

  const trackClick = () => {
    api.post(`/venues/${venue.id}/track-click`, { channel: "instagram" }).catch(() => {});
  };

  // Use images 1+ as "recent posts" so the hero image stays unique to the page header
  const photos = (venue.images || []).slice(1, 7);
  const handle = venue.instagram
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, "")
    .replace(/\/+$/, "")
    .replace(/^@?/, "@");

  return (
    <section className="mt-12" data-testid="instagram-preview">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)" }}>
            <Instagram className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg text-navy">From {handle}</div>
            <div className="text-xs text-neutral-500">Recent moments at {venue.name}</div>
          </div>
        </div>
        <a
          href={venue.instagram}
          target="_blank"
          rel="noreferrer"
          onClick={trackClick}
          className="inline-flex items-center gap-2 rounded-full text-white px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
          style={{ background: "linear-gradient(135deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)" }}
          data-testid="instagram-follow-cta"
        >
          <Instagram className="w-4 h-4" /> Follow on Instagram
        </a>
      </div>

      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {photos.map((src, i) => (
            <motion.a
              key={src || `ig-${i}`}
              href={venue.instagram}
              target="_blank"
              rel="noreferrer"
              onClick={trackClick}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group relative aspect-square overflow-hidden rounded-xl bg-sand/30 block"
              data-testid={`instagram-tile-${i}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.a>
          ))}
        </div>
      ) : (
        <a
          href={venue.instagram}
          target="_blank"
          rel="noreferrer"
          onClick={trackClick}
          className="block bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 text-center border border-pink-100 hover:border-pink-300 transition-colors"
        >
          <Instagram className="w-8 h-8 mx-auto text-pink-500 mb-3" />
          <p className="text-navy font-medium">See latest posts from {handle}</p>
          <p className="text-xs text-neutral-500 mt-1">Opens in Instagram</p>
        </a>
      )}
    </section>
  );
}
